import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { toLocalInput, fromLocalInput } from "@/lib/calendar";

// Shared modal for driver-managed events (personal_ride OR unavailability).
// Props:
//   mode: "personal_ride" | "unavailability"
//   initial: null | existing event object (for edit)
//   anchorDate: Date used when creating a new event
export default function CalendarEventModal({ open, onClose, mode, initial, anchorDate, onSaved, onDeleted }) {
  const isEdit = !!(initial && initial.source === "manual");
  const type = isEdit ? initial.event_type : mode;
  const isPersonal = type === "personal_ride";

  const [form, setForm] = useState({
    start_datetime: "",
    end_datetime: "",
    client_name: "",
    client_phone: "",
    pickup_location: "",
    dropoff_location: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        start_datetime: toLocalInput(new Date(initial.start_datetime)),
        end_datetime: toLocalInput(new Date(initial.end_datetime)),
        client_name: initial.client_name || "",
        client_phone: initial.client_phone || "",
        pickup_location: initial.pickup_location || "",
        dropoff_location: initial.dropoff_location || "",
        notes: initial.notes || "",
      });
    } else {
      const base = anchorDate ? new Date(anchorDate) : new Date();
      base.setMinutes(0, 0, 0);
      base.setHours(Math.max(base.getHours(), 9));
      const end = new Date(base.getTime() + (isPersonal ? 2 : 3) * 60 * 60 * 1000);
      setForm({
        start_datetime: toLocalInput(base),
        end_datetime: toLocalInput(end),
        client_name: "", client_phone: "",
        pickup_location: "", dropoff_location: "",
        notes: "",
      });
    }
  }, [open, initial, anchorDate, isEdit, isPersonal]);

  const save = async () => {
    if (!form.start_datetime || !form.end_datetime) {
      toast.error("Renseignez un début et une fin"); return;
    }
    if (isPersonal && !form.client_phone.trim()) {
      toast.error("Le téléphone client est obligatoire"); return;
    }
    setSaving(true);
    const payload = {
      event_type: type,
      start_datetime: fromLocalInput(form.start_datetime),
      end_datetime: fromLocalInput(form.end_datetime),
      client_name: form.client_name,
      client_phone: form.client_phone,
      pickup_location: form.pickup_location,
      dropoff_location: form.dropoff_location,
      notes: form.notes,
    };
    try {
      if (isEdit) {
        await api.put(`/calendar/events/${initial.id}`, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.post("/calendar/events", payload);
        toast.success(isPersonal ? "Course personnelle ajoutée" : "Indisponibilité ajoutée");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await api.delete(`/calendar/events/${initial.id}`);
      toast.success("Événement supprimé");
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="calendar-event-modal" className="bg-[#121212] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isPersonal ? (isEdit ? "Modifier la course personnelle" : "Nouvelle course personnelle")
                        : (isEdit ? "Modifier l'indisponibilité" : "Nouvelle indisponibilité")}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {isPersonal ? "Ajoutez une course hors dispatch à votre planning." : "Bloquez une plage horaire dans votre planning."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Début" type="datetime-local" testid="event-start"
              value={form.start_datetime} onChange={(v) => setForm((p) => ({ ...p, start_datetime: v }))} />
            <Field label="Fin" type="datetime-local" testid="event-end"
              value={form.end_datetime} onChange={(v) => setForm((p) => ({ ...p, end_datetime: v }))} />
          </div>

          {isPersonal && (
            <>
              <Field label="Nom client (optionnel)" testid="event-client-name"
                value={form.client_name} onChange={(v) => setForm((p) => ({ ...p, client_name: v }))} placeholder="Prénom Nom" />
              <Field label="Téléphone client *" testid="event-client-phone"
                value={form.client_phone} onChange={(v) => setForm((p) => ({ ...p, client_phone: v }))} placeholder="+33 6 XX XX XX XX" />
              <Field label="Lieu de prise en charge" testid="event-pickup"
                value={form.pickup_location} onChange={(v) => setForm((p) => ({ ...p, pickup_location: v }))} />
              <Field label="Destination" testid="event-dropoff"
                value={form.dropoff_location} onChange={(v) => setForm((p) => ({ ...p, dropoff_location: v }))} />
            </>
          )}

          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
            <textarea data-testid="event-notes"
              value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              rows={3}
              className="w-full rounded-md bg-[#1A1A1A] border border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none px-3 py-2 text-sm"
              placeholder={isPersonal ? "Détails utiles…" : "Raison (optionnel)"} />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {isEdit && (
            <Button data-testid="event-delete" variant="outline" onClick={remove}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10">Supprimer</Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">Annuler</Button>
          <Button data-testid="event-save" onClick={save} disabled={saving}
            className="bg-amber-500 text-black font-semibold hover:bg-amber-600">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-zinc-400 text-xs uppercase tracking-wider">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
    </div>
  );
}
